import { useEffect, useId, useState } from 'react';
import { C } from '@ridgewill/brand';
import {
  ApiError,
  acknowledgeFamilyAdult,
  getFamily,
  inviteToFamily,
  leaveFamilyGroup,
  markFamilyNoticesRead,
  startFamilyGroup,
  withdrawFamilyInvitation,
} from '../lib/api.js';
import {
  ACKNOWLEDGMENT,
  GUARDIAN_DOES,
  GUARDIAN_NEVER,
  GUARDIAN_SEES,
  dependentPath,
  initialOfName,
  leaveCopy,
  memberDetail,
  nameOf,
  noticeText,
  type FamilyGroup,
  type FamilyMember,
  type FamilyRole,
  type MyFamily,
} from '../lib/family.js';
import { formatDay } from '../lib/subscriptions.js';
import { Modal } from '../components/Modal.js';
import { Field, Note, Problem, button, input } from '../components/form.js';
import { Picture } from './Connections.js';

/**
 * The Family Group section (RidgeWill/account#117), at `/family`.
 *
 * ── TWO VIEWS OF ONE GROUP ───────────────────────────────────────────
 *
 * A guardian sees the guardians and dependents, the open invitations, and how
 * many agreements are waiting for them per dependent; they add people and open
 * a dependent's page. A dependent sees their guardians, exactly what a guardian
 * can see and do (the privacy policy's list), and the way out.
 *
 * ── AT 18 THEY STAY (Jon, 2026-10-12) ────────────────────────────────
 *
 * When auth says the acknowledgment is due, the dependent is asked to confirm
 * they take over their agreements, and leaving stays a separate choice. Age is
 * auth's answer; nothing here is typed or worked out.
 *
 * Nobody under 21 is offered a group of their own. `mayStart` is auth's.
 */
export function Family() {
  const [family, setFamily] = useState<MyFamily | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const f = await getFamily();
      setFamily({ mayStart: Boolean(f.mayStart), groups: f.groups ?? [], notices: f.notices ?? [] });
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your Family Group.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  if (error && !family) return <Problem>{error}</Problem>;
  if (!family) return <Note>Loading…</Note>;

  return (
    <section style={{ maxWidth: 640 }}>
      <Notices family={family} onRead={load} />
      {family.groups.length === 0 ? (
        <StartGroup mayStart={family.mayStart} onStarted={load} />
      ) : (
        family.groups.map((g) =>
          g.myRole === 'guardian' ? (
            <GuardianView key={g.id} group={g} onChanged={load} />
          ) : (
            <DependentSelf key={g.id} group={g} onChanged={load} />
          ),
        )
      )}
    </section>
  );
}

function Notices({ family, onRead }: { family: MyFamily; onRead: () => void }) {
  // `adult-acknowledgment-due` is left out: while it is due, the dependent's
  // own view says so with the button to act on it, and saying it twice is noise.
  const unread = family.notices.filter((n) => !n.read && n.kind !== 'adult-acknowledgment-due' && noticeText(n));
  if (unread.length === 0) return null;
  return (
    <div role="status" style={st.notices}>
      <ul style={{ margin: 0, paddingLeft: '1.1rem', display: 'grid', gap: '.35rem' }}>
        {unread.map((n) => (
          <li key={n.id}>
            {noticeText(n)} <span style={{ color: C.textMid }}>{formatDay(n.createdAt)}</span>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={() => void markFamilyNoticesRead(unread.map((n) => n.id)).then(onRead, onRead)}
        style={st.linkButton}
      >
        Dismiss
      </button>
    </div>
  );
}

/**
 * No group yet: the page "Create family group" opens.
 *
 * It says what a family group is for, once and plainly (Jon, 2026-10-13): it
 * brings people in a family together, and guardians can accept agreements for
 * members under 18 and see their connections and privacy settings. Only
 * someone auth says may create one is offered the button; anyone else
 * arriving by a link gets the same explanation and who can create one.
 */
function StartGroup({ mayStart, onStarted }: { mayStart: boolean; onStarted: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const purpose = (
    <p style={st.p}>
      A family group brings together the people in your family on Ridgewill. Guardians in the group can accept
      agreements for members under 18, and see their connections and privacy settings.
    </p>
  );

  if (!mayStart) {
    return (
      <>
        <h2 style={st.h2}>Family groups</h2>
        {purpose}
        <Note>
          A parent or legal guardian 21 or older can create one. If someone adds you to their family group, you
          will get an email.
        </Note>
      </>
    );
  }

  return (
    <>
      <h2 style={st.h2}>Create a family group</h2>
      {purpose}
      {error && <Problem>{error}</Problem>}
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setError(null);
          startFamilyGroup().then(
            () => onStarted(),
            (e: unknown) => {
              setError(e instanceof Error ? e.message : 'The family group could not be created.');
              setBusy(false);
            },
          );
        }}
        style={st.primary(busy)}
      >
        {busy ? 'Creating…' : 'Create family group'}
      </button>
    </>
  );
}

function GuardianView({ group, onChanged }: { group: FamilyGroup; onChanged: () => void }) {
  const [inviting, setInviting] = useState<FamilyRole | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pending = (kind: FamilyRole) => group.invitations.filter((i) => i.kind === kind);

  async function withdraw(id: string) {
    setBusy(id);
    setError(null);
    try {
      await withdrawFamilyInvitation(group.id, id);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That invitation could not be withdrawn.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <h2 style={st.h2}>Family Group</h2>
      {error && <Problem>{error}</Problem>}

      <h3 style={st.h3}>Guardians</h3>
      <ul style={st.list}>
        {group.guardians.map((m) => (
          <MemberRow key={m.userId} member={m} myRole="guardian" />
        ))}
        {pending('guardian').map((i) => (
          <InvitationRow key={i.id} email={i.email} expiresAt={i.expiresAt} busy={busy === i.id} onWithdraw={() => void withdraw(i.id)} />
        ))}
      </ul>

      <h3 style={st.h3}>Family members</h3>
      {group.dependents.length === 0 && pending('dependent').length === 0 ? (
        <Note>No one yet. Add a family member to get started.</Note>
      ) : (
        <ul style={st.list}>
          {group.dependents.map((m) => (
            <MemberRow key={m.userId} member={m} myRole="guardian" href={dependentPath(m.userId)} />
          ))}
          {pending('dependent').map((i) => (
            <InvitationRow key={i.id} email={i.email} expiresAt={i.expiresAt} busy={busy === i.id} onWithdraw={() => void withdraw(i.id)} />
          ))}
        </ul>
      )}

      <div style={st.actions}>
        <button type="button" onClick={() => setInviting('dependent')} style={st.button}>
          Add a family member
        </button>
        <button type="button" onClick={() => setInviting('guardian')} style={st.button}>
          Add a guardian
        </button>
      </div>

      <div style={st.out}>
        <button type="button" onClick={() => setLeaving(true)} style={st.quiet}>
          Leave the Family Group
        </button>
      </div>

      <InviteSheet
        kind={inviting}
        groupId={group.id}
        onClose={() => setInviting(null)}
        onSent={() => {
          setInviting(null);
          onChanged();
        }}
      />
      <GuardianLeaveDialog open={leaving} groupId={group.id} onClose={() => setLeaving(false)} onLeft={onChanged} />
    </>
  );
}

/** A dependent's own view of their group. */
function DependentSelf({ group, onChanged }: { group: FamilyGroup; onChanged: () => void }) {
  const me = group.dependents[0];
  const [leaving, setLeaving] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);

  // The connection page's "Leave Family Group" links here (`/family#leave`).
  useEffect(() => {
    if (window.location.hash === '#leave') setLeaving(true);
  }, []);

  const acknowledged = Boolean(me?.adultAcknowledgedAt);
  const due = Boolean(me?.acknowledgmentDue) && !acknowledged;
  /** auth's answer. Absent from an older auth: the old rule, adult only once told. */
  const adult = me?.guardianAccepts === undefined ? due || acknowledged : !me.guardianAccepts;

  return (
    <>
      <h2 style={st.h2}>Your Family Group</h2>

      {due && (
        <div style={st.callout}>
          <p style={{ margin: 0 }}>
            You are now 18 and accept your own agreements. Confirm that you take over the ones your parent or
            guardian accepted for you. You can stay in your Family Group.
          </p>
          <button type="button" onClick={() => setAcknowledging(true)} style={{ ...st.button, marginTop: '.75rem' }}>
            Take over your agreements
          </button>
        </div>
      )}
      {acknowledged && me?.adultAcknowledgedAt && (
        <Note>You handle your own agreements since {formatDay(me.adultAcknowledgedAt)}.</Note>
      )}

      <h3 style={st.h3}>Your guardians</h3>
      <ul style={st.list}>
        {group.guardians.map((m) => (
          <MemberRow key={m.userId} member={m} myRole="dependent" />
        ))}
      </ul>
      {group.guardians.length === 0 && (
        <Note>
          Your Family Group has no guardian right now. Anything that needs one is locked until a guardian joins
          or another Family Group adds you.
        </Note>
      )}

      <GuardianCanSee />

      <div style={st.out}>
        <button type="button" onClick={() => setLeaving(true)} style={st.quiet}>
          Leave the Family Group
        </button>
      </div>

      <AcknowledgeDialog
        open={acknowledging}
        groupId={group.id}
        onClose={() => setAcknowledging(false)}
        onDone={() => {
          setAcknowledging(false);
          onChanged();
        }}
      />
      <LeaveDialog
        open={leaving}
        groupId={group.id}
        adult={adult}
        acknowledged={!due}
        onClose={() => {
          setLeaving(false);
          if (window.location.hash === '#leave') {
            window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
          }
        }}
        onLeft={onChanged}
      />
    </>
  );
}

/** The fixed list, in the privacy policy's words. Also on the invitation page. */
export function GuardianCanSee({ guardianName = 'A guardian' }: { guardianName?: string }) {
  return (
    <div style={st.card}>
      <h3 style={{ ...st.h3, marginTop: 0 }}>{guardianName === 'A guardian' ? 'What a guardian can see' : `${guardianName} can see`}</h3>
      <ul style={st.bullets}>
        {GUARDIAN_SEES.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ul>
      <h3 style={st.h3}>{guardianName === 'A guardian' ? 'What a guardian can do' : `${guardianName} can`}</h3>
      <ul style={st.bullets}>
        {GUARDIAN_DOES.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ul>
      <Note>{GUARDIAN_NEVER}</Note>
    </div>
  );
}

function MemberRow({ member: m, myRole, href }: { member: FamilyMember; myRole: FamilyRole; href?: string }) {
  const name = nameOf(m);
  return (
    <li style={st.row}>
      <div style={{ display: 'flex', gap: '.75rem', alignItems: 'flex-start', minWidth: 0, flex: '1 1 16rem' }}>
        <Picture image={m.image} initial={initialOfName(name)} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
            {href ? (
              <a href={href} style={{ color: 'inherit', textUnderlineOffset: '.2em' }}>
                {name}
              </a>
            ) : (
              name
            )}
          </div>
          <Note>{memberDetail(m, myRole)}</Note>
        </div>
      </div>
    </li>
  );
}

function InvitationRow({
  email,
  expiresAt,
  busy,
  onWithdraw,
}: {
  email: string | null;
  expiresAt: string;
  busy: boolean;
  onWithdraw: () => void;
}) {
  return (
    <li style={st.row}>
      <div style={{ minWidth: 0, flex: '1 1 16rem' }}>
        <div style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{email ?? 'Invitation'}</div>
        <Note>Invited. The invitation stays open until {formatDay(expiresAt)}.</Note>
      </div>
      <button type="button" disabled={busy} onClick={onWithdraw} style={st.button}>
        {busy ? 'Withdrawing…' : 'Withdraw'}
      </button>
    </li>
  );
}

/**
 * Where the member sheet is (Jon, 2026-10-13): "Is this person under 18?",
 * then for yes "Are you their parent or legal guardian?", then the address.
 * A no to the second question stops: only a guardian may add someone under
 * 18. auth records both answers and refuses the same combinations.
 */
type MemberStep = 'age' | 'guardian' | 'stopped' | 'email';

/**
 * Add a family member or a guardian (account#117 screen 3). The member sheet
 * asks the two questions first (auth#627); the guardian sheet goes straight to
 * the address.
 */
function InviteSheet({
  kind,
  groupId,
  onClose,
  onSent,
}: {
  kind: FamilyRole | null;
  groupId: string;
  onClose: () => void;
  onSent: () => void;
}) {
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<MemberStep>('age');
  const [under18, setUnder18] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [last, setLast] = useState<FamilyRole>('dependent');
  const id = useId();

  useEffect(() => {
    if (kind) {
      setLast(kind);
      setEmail('');
      setStep('age');
      setUnder18(false);
      setError(null);
    }
  }, [kind]);

  const shown = kind ?? last;
  const dependent = shown === 'dependent';
  const ready = email.trim().includes('@');

  async function send() {
    setBusy(true);
    setError(null);
    try {
      await inviteToFamily(groupId, {
        kind: shown,
        email: email.trim(),
        ...(dependent ? { under18, ...(under18 ? { isGuardian: true } : {}) } : {}),
      });
      onSent();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That invitation could not be sent.');
    } finally {
      setBusy(false);
    }
  }

  const title = dependent ? 'Add a family member' : 'Add a guardian';
  const cancel = (
    <button type="button" disabled={busy} onClick={onClose} style={st.button}>
      Cancel
    </button>
  );

  if (dependent && step !== 'email') {
    return (
      <Modal open={kind !== null} title={title} onClose={onClose}>
        {step === 'age' && (
          <Question
            prompt="Is this person under 18?"
            onYes={() => {
              setUnder18(true);
              setStep('guardian');
            }}
            onNo={() => {
              setUnder18(false);
              setStep('email');
            }}
          >
            {cancel}
          </Question>
        )}
        {step === 'guardian' && (
          <Question
            prompt="Are you their parent or legal guardian?"
            onYes={() => setStep('email')}
            onNo={() => setStep('stopped')}
          >
            <button type="button" onClick={() => setStep('age')} style={st.button}>
              Back
            </button>
          </Question>
        )}
        {step === 'stopped' && (
          <>
            <p style={st.p}>
              Sorry, you can't add this person. Only a parent or legal guardian can add someone under 18 to a Family
              Group.
            </p>
            <div style={st.actions}>
              <button type="button" onClick={onClose} style={st.primary(false)}>
                Close
              </button>
              <button type="button" onClick={() => setStep('guardian')} style={st.button}>
                Back
              </button>
            </div>
          </>
        )}
      </Modal>
    );
  }

  return (
    <Modal open={kind !== null} title={title} onClose={onClose}>
      <p style={st.p}>
        {!dependent
          ? 'Another parent or legal guardian, 21 or older, who will help manage this Family Group.'
          : under18
            ? "You're adding someone under 18 as their parent or legal guardian. They don't need a Ridgewill account yet."
            : "You're adding someone 18 or older. They don't need a Ridgewill account yet."}
      </p>
      <Field label="Their email address" id={`${id}-email`}>
        <input
          id={`${id}-email`}
          type="email"
          autoComplete="off"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={input}
        />
      </Field>
      <Note>
        {!dependent
          ? "They'll get one email and have to accept. They need to be 21 or older."
          : under18
            ? "They'll get one email and have to accept. Before they join, they're shown what guardians can see. You accept agreements for them until they turn 18."
            : "They'll get one email and have to accept. Before they join, they're shown what guardians can see. They accept their own agreements."}
      </Note>
      {error && <Problem>{error}</Problem>}
      <div style={st.actions}>
        <button type="button" disabled={!ready || busy} onClick={() => void send()} style={st.primary(!ready || busy)}>
          {busy ? 'Sending…' : 'Send invitation'}
        </button>
        {dependent && (
          <button type="button" disabled={busy} onClick={() => setStep(under18 ? 'guardian' : 'age')} style={st.button}>
            Back
          </button>
        )}
        {cancel}
      </div>
    </Modal>
  );
}

/** One yes-or-no question in the member sheet, with the sheet's other buttons after. */
function Question({
  prompt,
  onYes,
  onNo,
  children,
}: {
  prompt: string;
  onYes: () => void;
  onNo: () => void;
  children: React.ReactNode;
}) {
  return (
    <>
      <p style={{ ...st.p, color: 'inherit', fontWeight: 600 }}>{prompt}</p>
      <div style={st.actions}>
        <button type="button" onClick={onYes} style={st.button}>
          Yes
        </button>
        <button type="button" onClick={onNo} style={st.button}>
          No
        </button>
        {children}
      </div>
    </>
  );
}

/**
 * A dependent leaves (account#117 screen 5). Two versions by age, from auth:
 * 18 or older confirms they take over their agreements (if they have not
 * already); under 18 is told what locks before they confirm.
 *
 * If auth knows the member is 18 before the portal does (a birthday today, or a
 * changed date the notice job has not reached), it refuses with
 * `ACKNOWLEDGMENT_REQUIRED`, and the dialog switches to the 18 version rather
 * than showing an error.
 */
export function LeaveDialog({
  open,
  groupId,
  adult: adultFromAuth,
  acknowledged,
  onClose,
  onLeft,
}: {
  open: boolean;
  groupId: string;
  adult: boolean;
  acknowledged: boolean;
  onClose: () => void;
  onLeft: () => void;
}) {
  const [adult, setAdult] = useState(adultFromAuth);
  /** Whether the hand-over acknowledgment is owed: auth's word, or its refusal saying so. */
  const [owed, setOwed] = useState(!acknowledged);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const id = useId();

  useEffect(() => {
    if (open) {
      setAdult(adultFromAuth);
      setOwed(!acknowledged);
      setChecked(false);
      setError(null);
      setDone(null);
    }
  }, [open, adultFromAuth, acknowledged]);

  const copy = leaveCopy(adult, !owed);
  const ready = copy.check === null || checked;

  async function leave() {
    setBusy(true);
    setError(null);
    try {
      await leaveFamilyGroup(groupId, adult && owed);
      setDone(
        adult
          ? 'You have left the Family Group. Your agreements are yours now.'
          : 'You have left the Family Group. Your guardians have been told. Ask a parent or guardian to add you to their Family Group to use anything that needs one.',
      );
    } catch (e) {
      if (e instanceof ApiError && e.code === 'ACKNOWLEDGMENT_REQUIRED') {
        setAdult(true);
        setOwed(true);
        setChecked(false);
      } else {
        setError(e instanceof Error ? e.message : 'You could not leave the Family Group.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title="Leave the Family Group?"
      onClose={() => {
        if (done) onLeft();
        onClose();
      }}
    >
      {done ? (
        <>
          <p role="status" style={st.p}>
            {done}
          </p>
          <button
            type="button"
            onClick={() => {
              onLeft();
              onClose();
            }}
            style={st.button}
          >
            Close
          </button>
        </>
      ) : (
        <>
          <p style={st.p}>{copy.lead}</p>
          {copy.check && (
            <label htmlFor={`${id}-ack`} style={st.check}>
              <input id={`${id}-ack`} type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
              <span>{copy.check}</span>
            </label>
          )}
          {error && <Problem>{error}</Problem>}
          <div style={st.actions}>
            <button type="button" disabled={!ready || busy} onClick={() => void leave()} style={st.primary(!ready || busy)}>
              {busy ? 'Leaving…' : 'Leave the Family Group'}
            </button>
            <button type="button" disabled={busy} onClick={onClose} style={st.button}>
              Cancel
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

/** The acknowledgment at 18, staying in the group. */
function AcknowledgeDialog({
  open,
  groupId,
  onClose,
  onDone,
}: {
  open: boolean;
  groupId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = useId();

  useEffect(() => {
    if (open) {
      setChecked(false);
      setError(null);
    }
  }, [open]);

  return (
    <Modal open={open} title="Take over your agreements" onClose={onClose}>
      <p style={st.p}>
        You stay in your Family Group, and your guardians keep the same view while you stay. You can leave at
        any time.
      </p>
      <label htmlFor={`${id}-ack`} style={st.check}>
        <input id={`${id}-ack`} type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
        <span>{ACKNOWLEDGMENT}</span>
      </label>
      {error && <Problem>{error}</Problem>}
      <div style={st.actions}>
        <button
          type="button"
          disabled={!checked || busy}
          onClick={() => {
            setBusy(true);
            setError(null);
            acknowledgeFamilyAdult(groupId).then(
              () => {
                setBusy(false);
                onDone();
              },
              (e: unknown) => {
                setBusy(false);
                setError(e instanceof Error ? e.message : 'That could not be recorded.');
              },
            );
          }}
          style={st.primary(!checked || busy)}
        >
          {busy ? 'Saving…' : 'Confirm'}
        </button>
        <button type="button" disabled={busy} onClick={onClose} style={st.button}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}

function GuardianLeaveDialog({
  open,
  groupId,
  onClose,
  onLeft,
}: {
  open: boolean;
  groupId: string;
  onClose: () => void;
  onLeft: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) setError(null);
  }, [open]);
  return (
    <Modal open={open} title="Leave the Family Group?" onClose={onClose}>
      <p style={st.p}>
        You will no longer be a guardian in this Family Group, and its other members will be told. A group with
        family members needs a guardian, so add another guardian or remove the family members first.
      </p>
      {error && <Problem>{error}</Problem>}
      <div style={st.actions}>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            setError(null);
            leaveFamilyGroup(groupId).then(
              () => {
                setBusy(false);
                onClose();
                onLeft();
              },
              (e: unknown) => {
                setBusy(false);
                setError(e instanceof Error ? e.message : 'You could not leave the Family Group.');
              },
            );
          }}
          style={st.button}
        >
          {busy ? 'Leaving…' : 'Leave the Family Group'}
        </button>
        <button type="button" disabled={busy} onClick={onClose} style={st.button}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}

export const st = {
  h2: { fontSize: '1.15rem', margin: '0 0 .5rem' },
  h3: { fontSize: '.95rem', fontWeight: 600, margin: '1.5rem 0 .25rem' },
  p: { margin: '0 0 1rem', lineHeight: 1.55, color: C.textMid, maxWidth: '62ch' },
  list: { listStyle: 'none', padding: 0, margin: '.25rem 0 0' },
  bullets: { margin: '.25rem 0 0', paddingLeft: '1.1rem', display: 'grid', gap: '.3rem', lineHeight: 1.5 },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap',
    padding: '.85rem 0',
    borderTop: `1px solid ${C.inputBorder}`,
  },
  actions: { display: 'flex', gap: '.6rem', flexWrap: 'wrap', marginTop: '1.25rem' },
  out: { marginTop: '2rem', paddingTop: '1rem', borderTop: `1px solid ${C.border}` },
  button: { ...button, marginTop: 0, whiteSpace: 'nowrap' },
  primary: (disabled: boolean): React.CSSProperties => ({
    ...button,
    marginTop: 0,
    fontWeight: 600,
    background: disabled ? 'transparent' : C.brand,
    color: disabled ? C.textMid : '#fff',
    borderColor: disabled ? C.inputBorder : C.brand,
    cursor: disabled ? 'default' : 'pointer',
  }),
  quiet: {
    background: 'none',
    border: 0,
    padding: 0,
    font: 'inherit',
    fontSize: '.9rem',
    color: C.dangerText,
    textDecoration: 'underline',
    textUnderlineOffset: '.15em',
    textAlign: 'left',
    cursor: 'pointer',
  },
  linkButton: {
    background: 'none',
    border: 0,
    padding: 0,
    font: 'inherit',
    color: C.brand,
    textDecoration: 'underline',
    textUnderlineOffset: '.15em',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  check: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '.7rem',
    margin: '.85rem 0 0',
    padding: '.75rem .9rem',
    border: `1px solid ${C.inputBorder}`,
    borderRadius: 6,
    cursor: 'pointer',
    lineHeight: 1.45,
  },
  callout: {
    margin: '0 0 1rem',
    padding: '.85rem 1rem',
    background: C.blueTint,
    color: C.blueText,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    lineHeight: 1.5,
  },
  notices: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '.5rem 1rem',
    flexWrap: 'wrap',
    margin: '0 0 1.25rem',
    padding: '.75rem 1rem',
    background: C.blueTint,
    color: C.blueText,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    fontSize: '.92rem',
    lineHeight: 1.45,
  },
  card: {
    marginTop: '1.5rem',
    padding: '1rem 1.1rem',
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    background: C.page,
  },
} satisfies Record<string, React.CSSProperties | ((d: boolean) => React.CSSProperties)>;
